import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { tournamentApi } from '@/api/tournament';
import { Brand } from '@/components/Brand';
import { Flaming7 } from '@/components/Flaming7';
import { useMeBootstrap } from '@/hooks/useMeBootstrap';
import {
  pendingSummaryTotal,
  useAdminPendingSummary,
} from '@/hooks/useAdminPendingSummary';
import { useSession } from '@/state/session';
import { cn } from '@/lib/utils';

/**
 * Persistent top navigation. Visible on every route except /login.
 * Composition:
 *   ┌───────────────┬─────────────────────────────┬──────────────────┐
 *   │ Logo + brand  │ Tournament · History · Me   │ {name} {ADMIN} ▾ │
 *   └───────────────┴─────────────────────────────┴──────────────────┘
 *
 * On screens < 640px the center collapses into a hamburger drawer. The
 * right-side identity stays visible.
 */
export function TopNav() {
  // Keep /me cache warm for the nav. Idempotent.
  useMeBootstrap();
  const storeUser = useSession((s) => s.user);
  const location = useLocation();

  // Surface the active tournament ID only for elevated admins, so we can
  // show a one-tap "Manage" link straight to that tournament's admin page.
  // Other users don't need this query firing.
  const isElevatedAdmin = !!(storeUser?.isAdmin && storeUser.sessionElevated);
  const activeQ = useQuery({
    queryKey: ['tournament', 'active'],
    queryFn: tournamentApi.active,
    enabled: isElevatedAdmin,
    staleTime: 30_000,
  });
  const adminManageTo =
    isElevatedAdmin && activeQ.data?.tournament
      ? `/admin/tournament/${activeQ.data.tournament.id}/manage`
      : null;

  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close menus on route change.
  useEffect(() => {
    setMenuOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 bg-cabinet-purple-ink/95 backdrop-blur">
      {/* The marquee is absolute-positioned at viewport center (left:50%,
          -translate-x-1/2) so it sits at the true horizontal midpoint of
          the screen regardless of how wide the left/right clusters get.
          The left and right clusters use normal flex around it and don't
          influence its placement. The page-level help icon (FloatingHelp
          in App.tsx) lives in the bottom-right and never touches this
          row, so the identity dropdown chevron is always reachable. */}
      <div className="relative mx-auto flex h-14 max-w-5xl items-center justify-between gap-3 px-4">
        {/* ── Left: hamburger (mobile) + logo + desktop nav links ─────── */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setDrawerOpen((s) => !s)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            className="sm:hidden h-11 w-11 rounded-md hover:bg-cabinet-cream/5 flex items-center justify-center text-cabinet-cream shrink-0"
          >
            <HamburgerIcon open={drawerOpen} />
          </button>
          <Link
            to="/"
            className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-cabinet-gold shrink-0"
            aria-label="Home"
          >
            <Flaming7 size={40} animated={false} />
          </Link>
          <nav className="hidden sm:flex items-center gap-1 ml-2">
            <NavItem to="/tournament/active">Tournament</NavItem>
            {adminManageTo && (
              <NavItem to={adminManageTo} highlight>
                Manage
              </NavItem>
            )}
            <NavItem to="/history">History</NavItem>
            <NavItem to="/me">Profile</NavItem>
          </nav>
        </div>

        {/* ── Center: marquee, absolute-centered on viewport ──────────── */}
        <Link
          to="/"
          className="absolute left-1/2 -translate-x-1/2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-cabinet-gold shrink-0"
          aria-label="Parks Slots home"
        >
          <Brand size="sm" textClassName="text-base sm:text-2xl" />
        </Link>

        {/* ── Right: identity + dropdown ──────────────────────────────── */}
        <div className="flex items-center gap-1 shrink-0">
          <div className="relative">
            <IdentityButton
              user={storeUser}
              open={menuOpen}
              onToggle={() => setMenuOpen((s) => !s)}
            />
            {menuOpen && (
              <Dropdown
                user={storeUser}
                onClose={() => setMenuOpen(false)}
                currentPath={location.pathname}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile drawer ──────────────────────────────────────────────── */}
      {drawerOpen && (
        <MobileDrawer
          onClose={() => setDrawerOpen(false)}
          adminManageTo={adminManageTo}
        />
      )}
    </header>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function NavItem({
  to,
  children,
  highlight,
}: {
  to: string;
  children: React.ReactNode;
  /** Tint differently so admin-only items visually separate from player nav. */
  highlight?: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'px-3 py-2 text-sm uppercase tracking-widest font-display rounded-md min-h-[44px] inline-flex items-center',
          isActive
            ? 'text-cabinet-gold-bright border-b-2 border-cabinet-gold'
            : highlight
              ? 'text-cabinet-flame-orange hover:text-cabinet-gold-bright'
              : 'text-cabinet-cream/70 hover:text-cabinet-cream',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function IdentityButton({
  user,
  open,
  onToggle,
}: {
  user: ReturnType<typeof useSession.getState>['user'];
  open: boolean;
  onToggle: () => void;
}) {
  // Hook order must be stable — call before any early returns.
  const pendingQ = useAdminPendingSummary();
  const pendingCount = pendingSummaryTotal(pendingQ.data);

  if (!user) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-1 px-3 py-2 rounded-md text-sm text-cabinet-cream/70 hover:text-cabinet-cream min-h-[44px]"
      >
        <span>Sign in</span>
        <ChevronIcon open={open} />
      </button>
    );
  }

  const elevated = user.isAdmin && user.sessionElevated;
  const displayName = user.displayName || user.email.split('@')[0];

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-haspopup="menu"
      className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md hover:bg-cabinet-cream/5 min-h-[44px]"
    >
      <span className="hidden sm:inline text-sm text-cabinet-cream max-w-[10rem] truncate">
        {displayName}
      </span>
      {elevated && (
        <span className="relative rounded-full bg-cabinet-gold text-cabinet-purple-ink px-2 py-0.5 text-[10px] uppercase tracking-widest font-display font-bold">
          Admin
          {pendingCount > 0 && (
            <span
              aria-label={`${pendingCount} ${pendingCount === 1 ? 'item' : 'items'} need attention`}
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-cabinet-flame-red text-cabinet-cream text-[10px] font-bold inline-flex items-center justify-center ring-2 ring-cabinet-purple-ink tabular-nums leading-none"
            >
              {pendingCount > 99 ? '99+' : pendingCount}
            </span>
          )}
        </span>
      )}
      <ChevronIcon open={open} />
    </button>
  );
}

function Dropdown({
  user,
  onClose,
  currentPath,
}: {
  user: ReturnType<typeof useSession.getState>['user'];
  onClose: () => void;
  currentPath: string;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setUser = useSession((s) => s.setUser);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Defer attachment one tick — the click that OPENED the menu is still
    // propagating; without this, the menu would close itself immediately.
    const id = window.setTimeout(() => {
      document.addEventListener('click', onDocClick);
    }, 0);
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const logout = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      setUser(null);
      qc.setQueryData(['me'], { user: null });
      navigate('/login');
    },
  });

  const dropAdmin = useMutation({
    mutationFn: authApi.dropAdmin,
    onSuccess: async () => {
      // Force a fresh /me fetch so sessionElevated flips to false everywhere.
      await qc.invalidateQueries({ queryKey: ['me'] });
      onClose();
    },
  });

  // Determine items by user state.
  type Item =
    | { kind: 'link'; label: string; to: string; danger?: boolean }
    | { kind: 'action'; label: string; onSelect: () => void; danger?: boolean; muted?: boolean }
    | { kind: 'divider' };
  const items: Item[] = [];

  // Only show dev-only entries when Vite was built in dev mode. Style Guide
  // stays accessible via direct URL regardless — this just hides the nav
  // entry on prod deploys.
  const isDev = import.meta.env.DEV;

  if (!user) {
    items.push({
      kind: 'link',
      label: 'Sign in',
      to: `/login?next=${encodeURIComponent(currentPath)}`,
    });
  } else if (!user.isAdmin) {
    items.push({ kind: 'action', label: 'Sign out', onSelect: () => logout.mutate(), danger: true });
  } else if (!user.sessionElevated) {
    items.push({
      kind: 'link',
      label: 'Re-enter admin password',
      to: `/login?next=${encodeURIComponent('/admin')}`,
    });
    items.push({ kind: 'divider' });
    items.push({ kind: 'action', label: 'Sign out', onSelect: () => logout.mutate(), danger: true });
  } else {
    // Common admin actions first.
    items.push({ kind: 'link', label: 'Admin home', to: '/admin' });
    items.push({ kind: 'link', label: 'Users', to: '/admin/users' });
    items.push({ kind: 'divider' });
    // Developer / QA section — Style guide hidden in production, Drop admin
    // tools always available but visually de-emphasized.
    if (isDev) {
      items.push({ kind: 'link', label: 'Style guide', to: '/style-guide' });
    }
    items.push({
      kind: 'action',
      label: 'Drop admin tools',
      onSelect: () => dropAdmin.mutate(),
      muted: true,
    });
    items.push({ kind: 'divider' });
    items.push({ kind: 'action', label: 'Sign out', onSelect: () => logout.mutate(), danger: true });
  }

  return (
    <div
      ref={ref}
      role="menu"
      className="absolute right-0 mt-2 w-56 rounded-lg bg-cabinet-purple-deep ring-1 ring-cabinet-gold/30 shadow-2xl shadow-black/60 overflow-hidden z-50"
    >
      {user && (
        <div className="px-3 py-2 border-b border-cabinet-cream/10">
          <p className="text-sm text-cabinet-cream truncate">{user.displayName || user.email.split('@')[0]}</p>
          <p className="text-[10px] text-cabinet-cream/50 truncate">{user.email}</p>
        </div>
      )}
      <ul className="py-1">
        {items.map((it, i) => {
          if (it.kind === 'divider') {
            return (
              <li
                key={i}
                role="separator"
                className="my-1 border-t border-cabinet-cream/10"
              />
            );
          }
          if (it.kind === 'link') {
            return (
              <li key={i}>
                <Link
                  to={it.to}
                  onClick={onClose}
                  role="menuitem"
                  className={cn(
                    'block px-3 py-2.5 text-sm hover:bg-cabinet-cream/5 min-h-[44px] flex items-center',
                    it.danger ? 'text-cabinet-flame-orange' : 'text-cabinet-cream',
                  )}
                >
                  {it.label}
                </Link>
              </li>
            );
          }
          return (
            <li key={i}>
              <button
                type="button"
                role="menuitem"
                onClick={it.onSelect}
                disabled={logout.isPending || dropAdmin.isPending}
                className={cn(
                  'w-full text-left px-3 py-2.5 text-sm hover:bg-cabinet-cream/5 min-h-[44px] disabled:opacity-60',
                  it.danger
                    ? 'text-cabinet-flame-orange'
                    : it.muted
                      ? 'text-cabinet-cream/60'
                      : 'text-cabinet-cream',
                )}
              >
                {it.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MobileDrawer({
  onClose,
  adminManageTo,
}: {
  onClose: () => void;
  adminManageTo: string | null;
}) {
  return (
    <div
      className="sm:hidden border-t border-cabinet-cream/10 bg-cabinet-purple-deep/95"
      role="dialog"
      aria-label="Navigation"
    >
      <nav className="flex flex-col py-2">
        <DrawerItem to="/tournament/active" onClick={onClose}>
          Tournament
        </DrawerItem>
        {adminManageTo && (
          <DrawerItem to={adminManageTo} onClick={onClose} highlight>
            Manage
          </DrawerItem>
        )}
        <DrawerItem to="/history" onClick={onClose}>
          History
        </DrawerItem>
        <DrawerItem to="/me" onClick={onClose}>
          Profile
        </DrawerItem>
      </nav>
    </div>
  );
}

function DrawerItem({
  to,
  onClick,
  children,
  highlight,
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'px-5 py-3 text-base font-display uppercase tracking-widest min-h-[48px] flex items-center',
          isActive
            ? 'text-cabinet-gold-bright bg-cabinet-cream/5'
            : highlight
              ? 'text-cabinet-flame-orange'
              : 'text-cabinet-cream/80',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return open ? (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      className={cn('transition-transform duration-150', open && 'rotate-180')}
    >
      <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
